<template>
  <lf-modal :title="modalTitle" title-border :close="close">
    <template #content>
      <div class="w-full">
        <div class="mt-3 p-6">
          <lf-input
            :placeholder="tokenTypeText"
            :value="token"
            :maxlength="255"
            name="client_token"
            data-cy="client-token-input"
          />
        </div>
        <hr />
        <div class="flex w-full justify-end space-x-4 p-6">
          <outline-button
            v-if="token"
            type="submit"
            class="cursor-pointer"
            :disabled="isSubmitting"
            @click="deleteUUID"
          >
            {{ $t("COMMON.DELETE") }}
          </outline-button>
          <primary-button
            type="submit"
            class="cursor-pointer"
            :disabled="isSubmitting"
            data-cy="save-uuid-button"
            @click="submitUUID"
          >
            {{ isEditMode ? $t("COMMON.SAVE") : $t("COMMON.ADD") }}
          </primary-button>
        </div>
      </div>
    </template>
  </lf-modal>
</template>
<script setup lang="ts">
import { computed, reactive } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";

import { callStoreAction, dispatchAction } from "@/helpers/vee-validate";
import { usePromiseWrapper } from "@/hooks/common";
import useBusinessesStore from "@/stores/businesses";
import { useIndividualProfileStore } from "@/stores/individualProfile";

import LfModal from "@/components/ui/Modal.vue";

import type { Entity } from "@/enums/entityProfiles";
import type { PropType } from "vue";

const props = defineProps({
  token: {
    type: String,
    default: "",
  },
  isPrimaryToken: {
    type: Boolean,
    default: true,
  },
  tokenId: {
    type: Number,
  },
  tokenIdx: {
    type: Number,
    required: false,
  },
  close: {
    type: Function as PropType<() => void>,
    required: true,
  },
  isBusinessProfileView: {
    type: Boolean,
    default: false,
  },
  profileType: {
    type: String as PropType<Entity>,
    default: "business",
  },
});

const emit = defineEmits<{
  additionalTokenCreated: [number, string]; // Token id, token value
  additionalTokenUpdated: [string]; // Token value
  additionalTokenDeleted: [];
}>();

const { dispatch } = useStore();
const { t } = useI18n();
const individualProfileStore = useIndividualProfileStore();
const businessesStore = useBusinessesStore();
const { activeBusinessEntityProfile } = storeToRefs(businessesStore);
const { currentIndividualProfile } = storeToRefs(individualProfileStore);

const isEditMode = computed(() => !!props.token);
const modeText = computed(() =>
  isEditMode.value ? t("COMMON.EDIT") : t("COMMON.ADD")
);
const tokenTypeText = computed(() =>
  props.isPrimaryToken
    ? t("DEALS.TRACKING.CLIENT_UUID")
    : t("DEALS.TRACKING.ADDITIONAL_UUID")
);
const modalTitle = computed(() => `${modeText.value} ${tokenTypeText.value}`);
const initialValues = reactive({ client_token: props.token });

const profile = computed(() =>
  props.profileType === "business"
    ? activeBusinessEntityProfile.value
    : currentIndividualProfile.value
);

const { handleSubmit, isSubmitting, setErrors } = useForm({
  initialValues,
});

const submitUUID = handleSubmit(
  async (
    values: {
      client_token: string;
      isPrimaryToken?: boolean;
      tokenId?: number;
      tokenIdx?: number;
      profileId?: string;
    },
    actions
  ) => {
    if (values.client_token.trim() === "") {
      deleteUUID();
      return;
    }

    let actionName;

    if (values.client_token === props.token) {
      closeModal();
      return;
    }

    if (props.isBusinessProfileView) {
      values.profileId = profile.value?.id;
      actionName = "updateClientTrackingToken";
    } else {
      actionName =
        isEditMode.value && !props.isPrimaryToken
          ? "applications/updateAdditionalToken"
          : "applications/addClientTrackingToken";

      values.isPrimaryToken = props.isPrimaryToken;
      values.tokenIdx = props.tokenIdx;
      values.tokenId = props.tokenId;
    }

    try {
      if (actionName === "updateClientTrackingToken") {
        await callStoreAction(
          values,
          actions,
          "updateClientTrackingToken",
          props.profileType === "business"
            ? businessesStore
            : individualProfileStore
        );
      } else {
        const token = await dispatchAction(values, actions, actionName);

        if (actionName === "applications/updateAdditionalToken") {
          emit("additionalTokenUpdated", values.client_token);
        } else if (actionName === "applications/addClientTrackingToken") {
          emit("additionalTokenCreated", token.id, values.client_token);
        }
      }

      dispatch("applications/syncActiveToAll");
    } catch {
      return;
    }
    closeModal();
  }
);

const { fetchWrapper: deleteUUID } = usePromiseWrapper(async () => {
  if (props.isBusinessProfileView) {
    const action =
      props.profileType === "business"
        ? businessesStore.updateClientTrackingToken
        : individualProfileStore.updateClientTrackingToken;

    await action({ profileId: profile.value!.id, client_token: "" });
  } else {
    if (props.isPrimaryToken) {
      await dispatch("applications/updatePrimaryTrackingToken", null);
    } else {
      await dispatch("applications/deleteAdditionalToken", props.tokenId);
      emit("additionalTokenDeleted");
    }
  }

  closeModal();
});

const closeModal = () => {
  setErrors({ client_token: "" });
  props.close();
};
</script>
